
import { PythonModule } from './types';

export const PYTHON_BASICS_MODULE: PythonModule = {
  id: "python-basics-1",
  title: "Introduction to Python and Variables",
  description: "Learn the fundamentals of Python programming including variables, data types, and basic operations.",
  content: `
# Introduction to Python and Variables

## What is Python?
Python is a high-level, interpreted programming language known for its simplicity and readability. It's widely used in web development, data science, artificial intelligence, and automation.

## Getting Started
Python programs are executed line by line by the Python interpreter. Let's start with the classic "Hello, World!" program.

## Variables and Data Types
Variables in Python are used to store data. Python is dynamically typed, meaning you don't need to declare the type of a variable explicitly.

### Basic Data Types:
- **Integers**: Whole numbers (e.g., 42, -17, 0)
- **Floats**: Decimal numbers (e.g., 3.14, -0.5, 2.0)
- **Strings**: Text data (e.g., "Hello", 'Python', "123")
- **Booleans**: True or False values

## Variable Assignment
Use the assignment operator (=) to assign values to variables:

\`\`\`python
name = "Alice"
age = 25
height = 5.6
is_student = True
\`\`\`

## Variable Naming Rules
- Must start with a letter or underscore
- Can contain letters, numbers, and underscores
- Case-sensitive (myVar and myvar are different)
- Cannot use Python keywords (if, for, while, etc.)

## Print Function
The print() function displays output to the console:

\`\`\`python
print("Hello, World!")
print(f"My name is {name} and I am {age} years old")
\`\`\`
  `,
  duration: "45 minutes",
  difficulty: "beginner",
  order: 1,
  tags: ["python", "basics", "variables", "data-types"],
  objectives: [
    "Understand what Python is and its applications",
    "Learn how to create and use variables",
    "Master basic data types in Python",
    "Use the print function to display output"
  ],
  pythonPractice: {
    task: "Create variables to store personal information and display them using f-strings",
    initialCode: `# Create variables for personal information
# name = 
# age = 
# city = 
# is_programmer = 

# Use print() with f-strings to display the information
`,
    expectedCode: `# Create variables for personal information
name = "John Doe"
age = 28
city = "New York"
is_programmer = True

# Use print() with f-strings to display the information
print(f"Name: {name}")
print(f"Age: {age}")
print(f"City: {city}")
print(f"Is programmer: {is_programmer}")
`,
    expectedOutput: {
      type: 'console',
      content: [
        "Name: John Doe",
        "Age: 28", 
        "City: New York",
        "Is programmer: True"
      ]
    },
    hints: [
      "Use the assignment operator (=) to create variables",
      "Strings should be enclosed in quotes",
      "Use f-strings with {variable_name} syntax for formatting"
    ]
  },
  pythonExamples: [
    {
      code: `# Different data types
name = "Python"
version = 3.9
is_popular = True
users = 1000000

print(f"Language: {name}")
print(f"Version: {version}")
print(f"Popular: {is_popular}")
print(f"Users: {users}")`,
      output: {
        type: 'console',
        content: [
          "Language: Python",
          "Version: 3.9",
          "Popular: True", 
          "Users: 1000000"
        ]
      },
      explanation: "This example demonstrates creating variables of different data types and using f-strings to format output."
    }
  ],
  quiz: {
    questions: [
      {
        id: "py-basic-1",
        question: "Which of the following is a valid variable name in Python?",
        options: ["2name", "_age", "class", "my-var"],
        correctAnswer: 1
      },
      {
        id: "py-basic-2", 
        question: "What data type is the value True in Python?",
        options: ["string", "integer", "boolean", "float"],
        correctAnswer: 2
      },
      {
        id: "py-basic-3",
        question: "Which operator is used to assign a value to a variable?",
        options: ["==", "=", "!=", "+="],
        correctAnswer: 1
      }
    ]
  }
};

export const PYTHON_CONTROL_FLOW_MODULE: PythonModule = {
  id: "python-basics-2",
  title: "Control Flow and Conditional Statements",
  description: "Master Python's conditional statements, loops, and control flow structures.",
  content: `
# Control Flow and Conditional Statements

## Conditional Statements
Conditional statements allow your program to make decisions based on certain conditions.

## if Statement
The if statement executes code only if a condition is True:

\`\`\`python
age = 18
if age >= 18:
    print("You are an adult")
\`\`\`

## if-else Statement
The else clause provides an alternative when the condition is False:

\`\`\`python
age = 16
if age >= 18:
    print("You can vote")
else:
    print("You cannot vote yet")
\`\`\`

## elif Statement
Use elif (else if) for multiple conditions:

\`\`\`python
score = 85
if score >= 90:
    print("Grade: A")
elif score >= 80:
    print("Grade: B")
elif score >= 70:
    print("Grade: C")
else:
    print("Grade: F")
\`\`\`

## Comparison Operators
- == (equal to)
- != (not equal to)
- < (less than)
- > (greater than)
- <= (less than or equal to)
- >= (greater than or equal to)

## Logical Operators
- and: Both conditions must be True
- or: At least one condition must be True
- not: Inverts the boolean value

\`\`\`python
age = 25
has_license = True

if age >= 18 and has_license:
    print("Can drive")
\`\`\`

## Loops
Loops allow you to repeat code multiple times.

### for Loop
Used to iterate over sequences:

\`\`\`python
for i in range(5):
    print(f"Count: {i}")
\`\`\`

### while Loop
Repeats while a condition is True:

\`\`\`python
count = 0
while count < 5:
    print(f"Count: {count}")
    count += 1
\`\`\`
  `,
  duration: "60 minutes",
  difficulty: "beginner",
  order: 2,
  tags: ["python", "control-flow", "conditionals", "loops"],
  objectives: [
    "Use if, elif, and else statements",
    "Understand comparison and logical operators",
    "Implement for and while loops",
    "Control program flow effectively"
  ],
  pythonPractice: {
    task: "Create a program that checks if a number is positive, negative, or zero, and counts from 1 to that number if positive",
    initialCode: `# Get a number (you can hardcode it for practice)
number = 5  # Change this value to test

# Check if the number is positive, negative, or zero
# Your if-elif-else logic here

# If the number is positive, count from 1 to that number
# Your for loop here
`,
    expectedCode: `# Get a number (you can hardcode it for practice)
number = 5  # Change this value to test

# Check if the number is positive, negative, or zero
if number > 0:
    print(f"{number} is positive")
    print("Counting from 1 to", number)
    for i in range(1, number + 1):
        print(i)
elif number < 0:
    print(f"{number} is negative")
else:
    print("The number is zero")
`,
    expectedOutput: {
      type: 'console',
      content: [
        "5 is positive",
        "Counting from 1 to 5",
        "1",
        "2", 
        "3",
        "4",
        "5"
      ]
    },
    hints: [
      "Use if, elif, else for the number check",
      "Use range(1, number + 1) to count from 1 to the number",
      "Remember that range() is exclusive of the end value"
    ]
  },
  pythonExamples: [
    {
      code: `# Grade calculator
score = 87

if score >= 90:
    grade = "A"
elif score >= 80:
    grade = "B"
elif score >= 70:
    grade = "C"
elif score >= 60:
    grade = "D"
else:
    grade = "F"

print(f"Score: {score}, Grade: {grade}")

# Count even numbers
print("Even numbers from 0 to 10:")
for num in range(0, 11):
    if num % 2 == 0:
        print(num)`,
      output: {
        type: 'console',
        content: [
          "Score: 87, Grade: B",
          "Even numbers from 0 to 10:",
          "0",
          "2",
          "4",
          "6",
          "8",
          "10"
        ]
      },
      explanation: "This example shows how to use conditional statements for grading and loops with conditions to filter even numbers."
    }
  ],
  quiz: {
    questions: [
      {
        id: "py-control-1",
        question: "What will be printed if x = 10 and the condition is 'if x > 5:'?",
        options: ["Nothing", "True", "The code inside the if block", "False"],
        correctAnswer: 2
      },
      {
        id: "py-control-2",
        question: "Which loop is best for iterating a specific number of times?",
        options: ["while loop", "for loop", "do-while loop", "infinite loop"],
        correctAnswer: 1
      },
      {
        id: "py-control-3",
        question: "What does the 'and' logical operator require?",
        options: ["One condition to be True", "Both conditions to be True", "All conditions to be False", "At least one condition to be False"],
        correctAnswer: 1
      }
    ]
  }
};

export const PYTHON_FUNCTIONS_MODULE: PythonModule = {
  id: "python-basics-3",
  title: "Functions and Code Organization",
  description: "Learn how to create reusable code with functions, parameters, and return values.",
  content: `
# Functions and Code Organization

## What are Functions?
Functions are reusable blocks of code that perform specific tasks. They help organize code, reduce repetition, and make programs more modular.

## Defining Functions
Use the \`def\` keyword to define a function:

\`\`\`python
def greet():
    print("Hello, World!")

# Call the function
greet()
\`\`\`

## Functions with Parameters
Parameters allow you to pass data to functions:

\`\`\`python
def greet_person(name):
    print(f"Hello, {name}!")

greet_person("Alice")
greet_person("Bob")
\`\`\`

## Multiple Parameters
Functions can accept multiple parameters:

\`\`\`python
def add_numbers(a, b):
    result = a + b
    print(f"{a} + {b} = {result}")

add_numbers(5, 3)
\`\`\`

## Return Values
Functions can return values using the \`return\` statement:

\`\`\`python
def multiply(a, b):
    return a * b

result = multiply(4, 7)
print(f"Result: {result}")
\`\`\`

## Default Parameters
You can provide default values for parameters:

\`\`\`python
def introduce(name, age=25):
    print(f"Hi, I'm {name} and I'm {age} years old")

introduce("Alice")  # Uses default age
introduce("Bob", 30)  # Uses provided age
\`\`\`

## Local vs Global Scope
Variables defined inside functions are local to that function:

\`\`\`python
global_var = "I'm global"

def my_function():
    local_var = "I'm local"
    print(global_var)  # Can access global
    print(local_var)   # Can access local

my_function()
# print(local_var)  # This would cause an error
\`\`\`

## Docstrings
Document your functions with docstrings:

\`\`\`python
def calculate_area(length, width):
    """
    Calculate the area of a rectangle.
    
    Args:
        length (float): The length of the rectangle
        width (float): The width of the rectangle
    
    Returns:
        float: The area of the rectangle
    """
    return length * width
\`\`\`
  `,
  duration: "50 minutes",
  difficulty: "beginner",
  order: 3,
  tags: ["python", "functions", "parameters", "scope"],
  objectives: [
    "Define and call functions in Python",
    "Use parameters and return values",
    "Understand function scope",
    "Write clear function documentation"
  ],
  pythonPractice: {
    task: "Create a calculator with functions for basic arithmetic operations",
    initialCode: `# Define functions for basic arithmetic operations
def add(a, b):
    # Return the sum of a and b
    pass

def subtract(a, b):
    # Return the difference of a and b
    pass

def multiply(a, b):
    # Return the product of a and b
    pass

def divide(a, b):
    # Return the division of a by b (handle division by zero)
    pass

# Test your functions
# Call each function with test values and print results
`,
    expectedCode: `# Define functions for basic arithmetic operations
def add(a, b):
    """Return the sum of a and b"""
    return a + b

def subtract(a, b):
    """Return the difference of a and b"""
    return a - b

def multiply(a, b):
    """Return the product of a and b"""
    return a * b

def divide(a, b):
    """Return the division of a by b"""
    if b == 0:
        return "Error: Division by zero"
    return a / b

# Test your functions
print(f"Addition: 10 + 5 = {add(10, 5)}")
print(f"Subtraction: 10 - 5 = {subtract(10, 5)}")
print(f"Multiplication: 10 * 5 = {multiply(10, 5)}")
print(f"Division: 10 / 5 = {divide(10, 5)}")
print(f"Division by zero: 10 / 0 = {divide(10, 0)}")
`,
    expectedOutput: {
      type: 'console',
      content: [
        "Addition: 10 + 5 = 15",
        "Subtraction: 10 - 5 = 5",
        "Multiplication: 10 * 5 = 50",
        "Division: 10 / 5 = 2.0",
        "Division by zero: 10 / 0 = Error: Division by zero"
      ]
    },
    hints: [
      "Use the return statement to send values back from functions",
      "Check if the divisor is zero before performing division",
      "Test your functions by calling them with different values"
    ]
  },
  pythonExamples: [
    {
      code: `def calculate_bmi(weight, height):
    """Calculate Body Mass Index"""
    bmi = weight / (height ** 2)
    return round(bmi, 2)

def bmi_category(bmi):
    """Determine BMI category"""
    if bmi < 18.5:
        return "Underweight"
    elif bmi < 25:
        return "Normal weight"
    elif bmi < 30:
        return "Overweight"
    else:
        return "Obese"

# Test the functions
weight = 70  # kg
height = 1.75  # meters

bmi = calculate_bmi(weight, height)
category = bmi_category(bmi)

print(f"Weight: {weight} kg")
print(f"Height: {height} m") 
print(f"BMI: {bmi}")
print(f"Category: {category}")`,
      output: {
        type: 'console',
        content: [
          "Weight: 70 kg",
          "Height: 1.75 m",
          "BMI: 22.86",
          "Category: Normal weight"
        ]
      },
      explanation: "This example demonstrates creating multiple functions that work together, using return values, and proper function documentation."
    }
  ],
  quiz: {
    questions: [
      {
        id: "py-func-1",
        question: "What keyword is used to define a function in Python?",
        options: ["function", "def", "define", "func"],
        correctAnswer: 1
      },
      {
        id: "py-func-2",
        question: "What does the 'return' statement do in a function?",
        options: ["Prints a value", "Ends the function and sends back a value", "Creates a variable", "Starts a loop"],
        correctAnswer: 1
      },
      {
        id: "py-func-3",
        question: "What happens to variables defined inside a function?",
        options: ["They become global", "They are local to the function", "They are deleted immediately", "They cause an error"],
        correctAnswer: 1
      }
    ]
  }
};
